import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loginWithEmailPassword } from "../common/api/loginApi";
import { hasAuthenticatedSession } from "../common/utils/sessionContext";
import { resolvePostLoginNavigation, resolveSessionLandingRoute } from "../common/utils/authSession";
import AlertMessage from "../common/components/AlertMessage";
import Backdrop from "@mui/material/Backdrop";
import * as Sentry from "@sentry/react";
import { FaEye, FaEyeSlash, FaArrowRight, FaEnvelope, FaLock } from "react-icons/fa";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isCaregiverDomain = (email) => {
  const domain = email.trim().toLowerCase().split("@")[1];
  return domain === "caregiver.com";
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginData, setLoginData] = useState({
    emailId: "",
    loginCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [loginBtnEnabled, setLoginBtnEnabled] = useState(true);
  const [showCode, setShowCode] = useState(false);

  const isLoginCode = isCaregiverDomain(loginData.emailId);

  useEffect(() => {
    const prefilledEmail = location.state?.email?.trim() || "";
    if (prefilledEmail) {
      const nextData = { emailId: prefilledEmail, loginCode: "" };
      setLoginData(nextData);
      updateLoginButtonState(nextData, isCaregiverDomain(prefilledEmail));
    }
  }, [location.state?.email]);

  useEffect(() => {
    if (!hasAuthenticatedSession()) {
      return;
    }

    navigate(resolveSessionLandingRoute());
  }, [navigate]);

  const updateLoginButtonState = (data, useLoginCode) => {
    if (!EMAIL_REGEX.test(data.emailId)) {
      setLoginBtnEnabled(true);
      return;
    }
    if (useLoginCode) {
      setLoginBtnEnabled(data.loginCode.length !== 4);
      return;
    }
    setLoginBtnEnabled(!data.loginCode.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loginBtnEnabled) {
      return;
    }

    setLoading(true);

    try {
      const loginRes = await loginWithEmailPassword({
        email: loginData.emailId,
        password: loginData.loginCode,
      });

      if (!loginRes.success) {
        setAlertStatusBase({
          message: loginRes.message,
          status: "400",
          origin: "center",
        });
        return;
      }

      const normalizedEmail = loginData.emailId.trim().toLowerCase();
      localStorage.setItem("userEmail", normalizedEmail);

      const navigation = await resolvePostLoginNavigation(loginRes, normalizedEmail);

      if (!navigation.success) {
        setAlertStatusBase({
          message: navigation.message,
          status: "400",
          origin: "center",
        });
        return;
      }

      navigate(navigation.path, navigation.state ? { state: navigation.state } : undefined);
    } catch (error) {
      Sentry.captureException(error);
      setAlertStatusBase({
        message: "Unable to sign in. Please try again.",
        status: "400",
        origin: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeAlert = () => {
    setAlertStatusBase("");
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password", { state: { email: loginData.emailId.trim() } });
  };

  const updateDetails = (e) => {
    const key = e.target.name;
    let value = e.target.value;
    value = value.replace(/^\s+/, "");

    if (key === "emailId") {
      value = value.replace(/[^0-9a-zA-Z@,.\-_/\s]/g, "");
      value = value.toLowerCase();
    } else if (key === "loginCode" && isCaregiverDomain(loginData.emailId)) {
      value = value.replace(/[^0-9]/g, "").slice(0, 4);
    }

    const nextData = {
      ...loginData,
      [key]: value,
    };

    setLoginData(nextData);
    updateLoginButtonState(nextData, isCaregiverDomain(nextData.emailId));
  };

  const passwordPlaceholder = isLoginCode ? "Enter your login code" : "password";

  return (
    <div className="outer-container bt-login-outer">
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}
      <div className="bt-login-page">
        <div className="bt-login-logo-wrap">
          <img src={require("../assets/Loginpage_logo.png")} className="bt-login-logo" alt="BridgeTree Digital Care Communications" />
        </div>

        {/* <header className="bt-login-header">
          <h1 className="bt-login-welcome">Welcome back</h1>
          <p className="bt-login-subtitle">Sign in with your email or login code to continue.</p>
        </header> */}

        <form onSubmit={handleSubmit} className="bt-login-form">
          <div className="bt-login-field-group">
            <label htmlFor="emailId" className="bt-login-label">
              Email
            </label>
            <div className="bt-login-input-wrap">
              <FaEnvelope className="bt-login-input-icon" aria-hidden="true" />
              <input
                id="emailId"
                type="email"
                name="emailId"
                className="bt-login-input"
                value={loginData.emailId}
                onChange={updateDetails}
                placeholder="Email"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="bt-login-field-group">
            <label htmlFor="loginCode" className="bt-login-label">
              Password
            </label>
            <div className="bt-login-input-wrap">
              <FaLock className="bt-login-input-icon" aria-hidden="true" />
              <input
                id="loginCode"
                type={showCode ? "text" : "password"}
                name="loginCode"
                className="bt-login-input bt-login-input--with-action"
                value={loginData.loginCode}
                onChange={updateDetails}
                placeholder={passwordPlaceholder}
                inputMode={isLoginCode ? "numeric" : "text"}
                maxLength={isLoginCode ? 4 : 75}
                autoComplete={isLoginCode ? "one-time-code" : "current-password"}
                required
              />
              <button
                type="button"
                className="bt-login-toggle-visibility"
                onClick={() => setShowCode(!showCode)}
                aria-label={showCode ? "Hide password" : "Show password"}
              >
                {showCode ? <FaEye /> : <FaEyeSlash />}
              </button>
            </div>
          </div>

          <div className="bt-login-options">
            <button type="button" className="bt-login-forgot" onClick={handleForgotPassword}>
              Forgot password?
            </button>
          </div>

          <button type="submit" className="bt-login-submit" disabled={loginBtnEnabled}>
            <span>Sign in</span>
            <FaArrowRight aria-hidden="true" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
